import { useState, type ReactNode } from 'react';
import { CONTROL_PANEL_PATHS, type OperatorPlan, type OperatorTenant } from '@forecastify/shared';
import { Button, Icon, Select, Stepper, cn, type IconName } from '@forecastify/ui';
import { useI18n, useT } from '../i18n';
import { formatMoney, formatNumber } from '../chrome/format';
import { BTN, EYEBROW, SELECT_SKIN, TONE } from '../chrome/tones';
import { featureName } from './feature-names';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ *Csomag és helyek* — two cards, each with *BEÁLLÍTÁS* in the corner.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE PLAN IS A DRAFT UNTIL SAVED, AS THE MOCKUP DRAWS IT.** The first version assigned the
 * moment the dropdown changed — one misclick moved a customer onto another price. The redesign
 * shows what the change WOULD do (*Új havi érték · korlát · funkciók*) and waits for *Mentés*.
 *
 * ⚠️⚠️ **"NO PLAN" STAYS AN OPTION, WHICH THE MOCKUP DOES NOT DRAW.** S6 made taking a plan away a
 * real choice — undoing a mistaken assignment is why it exists — and it sits last in the list.
 *
 * ⚠️ **Lowering a limit removes nobody**, and the warning says what it does do: nobody new can be
 * added until the limit rises or seats free up. That is the sentence an operator otherwise gets
 * wrong.
 */

type Write = (path: string, method: 'POST' | 'PUT' | 'DELETE', body?: unknown) => Promise<boolean>;

export function TenantPlan({
  tenant,
  plans,
  busy,
  write,
}: {
  readonly tenant: OperatorTenant;
  readonly plans: readonly OperatorPlan[] | null;
  readonly busy: boolean;
  readonly write: Write;
}): ReactNode {
  return (
    <>
      <PlanCard tenant={tenant} plans={plans} busy={busy} write={write} />
      <SeatsCard tenant={tenant} plans={plans} busy={busy} write={write} />
    </>
  );
}

function PlanCard({
  tenant,
  plans,
  busy,
  write,
}: {
  readonly tenant: OperatorTenant;
  readonly plans: readonly OperatorPlan[] | null;
  readonly busy: boolean;
  readonly write: Write;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  /* `null` = not editing: the field shows the tenant's own plan until somebody picks another. */
  const [draft, setDraft] = useState<string | null>(null);

  const current = tenant.planId ?? '';
  const chosen = draft ?? current;
  const dirty = draft !== null && draft !== current;
  const catalogue = plans ?? [];
  const plan = catalogue.find((candidate) => candidate.id === current);
  const drafted = catalogue.find((candidate) => candidate.id === chosen);

  const features = (candidate: OperatorPlan): string =>
    Object.entries(candidate.featureFlags)
      .filter(([, on]) => on)
      .map(([key]) => featureName(key, t))
      .join(', ');

  const effect = dirty
    ? drafted === undefined
      ? t('tenant.plan.noneHint')
      : t('tenant.plan.effect', {
          value: formatMoney(
            String(Number(drafted.seatPrice) * tenant.seatsUsed),
            drafted.currency,
            locale,
          ),
          limit:
            tenant.seatLimitOverride !== null
              ? t('tenant.plan.limitOverride', { count: tenant.seatLimitOverride })
              : drafted.seatLimit === null
                ? '∞'
                : formatNumber(drafted.seatLimit, locale),
          features: features(drafted),
        })
    : plan === undefined
      ? t('tenant.plan.noneHint')
      : t('tenant.plan.current', {
          price: formatMoney(plan.seatPrice, plan.currency, locale),
          features: features(plan),
        });

  return (
    <section aria-label={t('tenant.plan')} className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-center gap-2">
        <h3 className="text-[14px] font-medium">{t('tenant.plan')}</h3>
        <span className={cn('text-muted-foreground ml-auto', EYEBROW)}>{t('tenant.adjust')}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[300px] max-w-full">
          <Select
            aria-label={t('tenant.plan')}
            disabled={busy || plans === null}
            value={chosen}
            options={[
              ...catalogue
                .filter((candidate) => candidate.isActive || candidate.id === current)
                .map((candidate) => ({
                  value: candidate.id,
                  label: candidate.isActive
                    ? candidate.name
                    : `${candidate.name} · ${t('tenants.withdrawn')}`,
                  hint: formatMoney(candidate.seatPrice, candidate.currency, locale),
                })),
              { value: '', label: t('tenant.plan.none') },
            ]}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
            className={cn(SELECT_SKIN, 'border-input')}
          />
        </div>
        {dirty ? (
          <>
            <Button
              className={BTN}
              disabled={busy}
              onClick={() => {
                void write(CONTROL_PANEL_PATHS.assignPlan(tenant.id), 'PUT', {
                  planId: chosen === '' ? null : chosen,
                }).then((ok) => {
                  if (ok) setDraft(null);
                });
              }}
            >
              {busy ? t('tenant.plan.saving') : t('tenant.save')}
            </Button>
            <Button
              variant="ghost"
              className={BTN}
              onClick={() => {
                setDraft(null);
              }}
            >
              {t('tenant.cancel')}
            </Button>
          </>
        ) : null}
      </div>
      <span className="text-muted-foreground text-[12px]">{effect}</span>
      {plan !== undefined && !plan.isActive ? (
        <p className="bg-muted rounded-md px-3 py-2 text-[12.5px] text-pretty">
          {t('tenant.plan.withdrawnNote')}
        </p>
      ) : null}
    </section>
  );
}

function SeatsCard({
  tenant,
  plans,
  busy,
  write,
}: {
  readonly tenant: OperatorTenant;
  readonly plans: readonly OperatorPlan[] | null;
  readonly busy: boolean;
  readonly write: Write;
}): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  /* `null` = not editing, so a reload underneath does not fight what is on screen. */
  const [draft, setDraft] = useState<string | null>(null);

  const plan = (plans ?? []).find((candidate) => candidate.id === tenant.planId);
  const stored = tenant.seatLimitOverride === null ? '' : String(tenant.seatLimitOverride);
  const shown = draft ?? stored;
  const dirty = draft !== null && draft.trim() !== stored;
  const limit = tenant.seatLimit;
  const used = tenant.seatsUsed;
  const over = limit !== null && used > limit;
  const planLimit = plan?.seatLimit ?? null;
  const pct = limit === null ? 30 : limit === 0 ? 100 : Math.min(100, (used / limit) * 100);

  const warnings: { icon: IconName; tone: string; text: string }[] = [];
  if (over) {
    warnings.push({
      icon: 'triangle-alert',
      tone: 'bg-destructive/[0.07] text-destructive',
      text: t('tenant.seats.overCount', { used, limit, free: used - limit }),
    });
  }
  if (tenant.seatsPending > 0) {
    const after = used + tenant.seatsPending;
    const beyond = limit !== null && after > limit;
    warnings.push({
      icon: 'mail',
      tone: beyond ? TONE.warn : 'bg-muted',
      text: beyond
        ? t('tenant.seats.pendingBeyond', { count: tenant.seatsPending, after })
        : t('tenant.seats.pendingAfter', { count: tenant.seatsPending, after }),
    });
  }

  return (
    <section aria-label={t('tenant.seats')} className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-center gap-2">
        <h3 className="text-[14px] font-medium">{t('tenant.seats')}</h3>
        <span className={cn('text-muted-foreground ml-auto', EYEBROW)}>{t('tenant.adjust')}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            'text-[22px] font-semibold tracking-[-.015em] tabular-nums',
            over ? 'text-destructive' : '',
          )}
        >
          {formatNumber(used, locale)}
        </span>
        <span className="text-muted-foreground text-[13px] tabular-nums">
          / {limit === null ? t('tenant.seats.unlimited') : formatNumber(limit, locale)}
        </span>
        <span className="text-muted-foreground ml-auto text-[12px]">
          {tenant.seatLimitOverride !== null
            ? t('tenant.seats.fromOverride')
            : plan === undefined
              ? t('tenant.seats.fromPlan')
              : `${t('tenant.seats.fromPlan')} · ${plan.name}`}
        </span>
      </div>
      <div className="bg-muted h-1.5 overflow-hidden rounded-full">
        <div
          className={cn('h-full rounded-full', over ? 'bg-destructive' : 'bg-primary')}
          style={{ width: `${String(pct)}%` }}
        />
      </div>
      {warnings.map((warning) => (
        <div
          key={warning.text}
          className={cn('flex items-start gap-2 rounded-md px-3 py-2 text-[12.5px]', warning.tone)}
        >
          <Icon name={warning.icon} size={14} className="mt-px shrink-0" />
          <span className="text-pretty">{warning.text}</span>
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        <span className="text-[12.5px] font-medium">{t('tenant.seats.override')}</span>
        <Stepper
          value={shown}
          onChange={setDraft}
          min={0}
          placeholder={planLimit === null ? '∞' : String(planLimit)}
          decrementLabel={t('stepper.decrement')}
          incrementLabel={t('stepper.increment')}
          className="hover:border-input w-[120px] border-transparent shadow-none"
        />
        <span className="text-muted-foreground min-w-0 flex-1 text-[12px]">
          {t('tenant.seats.hint', {
            limit:
              planLimit === null ? t('tenants.invite.unlimited') : formatNumber(planLimit, locale),
          })}
        </span>
        {dirty ? (
          <Button
            className={BTN}
            disabled={busy}
            onClick={() => {
              const raw = shown.trim();
              void write(CONTROL_PANEL_PATHS.seatLimit(tenant.id), 'PUT', {
                seatLimit: raw === '' ? null : Number(raw),
              }).then((ok) => {
                if (ok) setDraft(null);
              });
            }}
          >
            {busy ? t('tenant.seats.saving') : t('tenant.save')}
          </Button>
        ) : null}
        {tenant.seatLimitOverride !== null && !dirty ? (
          <Button
            variant="ghost"
            className={BTN}
            disabled={busy}
            onClick={() => {
              void write(CONTROL_PANEL_PATHS.seatLimit(tenant.id), 'PUT', { seatLimit: null });
            }}
          >
            {t('tenant.seats.clear')}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
